"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { ShieldCheck, Activity, Facebook, Twitter, Linkedin, Instagram, MapPin, Phone, Mail, ArrowRight, CircleCheck, Cpu, Lock, Zap } from "lucide-react";
function NewsletterForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    successMessage
  } = useWvcForm();
  if (isSubmitted) {
    return <div data-section-id="36131" className="bg-primary/10 border border-primary/20 rounded-sm p-4 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2">
        <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-primary uppercase tracking-wider">Subscription Confirmed</p>
          <p className="text-xs text-muted-foreground mt-1">{successMessage}</p>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-3">
      <FormField control={control} name="email" rules={{
      required: "Corporate email is required for registration.",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email format."
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">Email Address</FormLabel>
            <FormControl>
              <div className="relative group">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                <Input placeholder="Professional email address..." className="pl-9 bg-background/50 border-input focus-visible:ring-primary/50 transition-all rounded-sm" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />
      <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg rounded-sm font-semibold tracking-wide" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Activity className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </> : <>
            Register for Insights <ArrowRight className="ml-2 h-4 w-4" />
          </>}
      </Button>
      <p className="text-[10px] text-muted-foreground flex items-center gap-1.5 font-mono uppercase">
        <Lock className="w-3 h-3" />
        Secure corporate data handling.
      </p>
    </Form>;
}
export default function SecurityIntelligenceFooter() {
  const currentYear = new Date().getFullYear();
  return <section id="footer-information" className="dark bg-background text-foreground border-t border-border relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />
      <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {}
          <div className="space-y-6">
            <Link to="/HomePage" className="block w-fit">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1581784/img/aoto_logo_white.png" alt="AOTO" className="h-8 w-auto object-contain" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs font-sans">
              AOTO is a premier international consultancy specializing in cultural tourism development, delivering integrated EPCO solutions for visionary destinations across the globe.
            </p>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 hover:bg-primary/10 transition-colors rounded-sm uppercase tracking-widest text-[10px]">
                <ShieldCheck className="w-3 h-3 mr-1" />
                Global Excellence Standards
              </Badge>
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-bold tracking-tight flex items-center gap-2 uppercase text-foreground">
              <Zap className="w-4 h-4 text-primary" />
              Corporate
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/HomePage" className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30 group-hover:bg-primary transition-colors" />
                  Home
                </Link>
              </li>
              <li>
                <Link to="#" className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30 group-hover:bg-primary transition-colors" />
                  About
                </Link>
              </li>
              <li>
                <Link to="/ProjectsPage" className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30 group-hover:bg-primary transition-colors" />
                  Projects
                </Link>
              </li>
              <li>
                <Link to="/InsightsPage" className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30 group-hover:bg-primary transition-colors" />
                  Insights
                </Link>
              </li>
            </ul>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-bold tracking-tight flex items-center gap-2 uppercase text-foreground">
              <MapPin className="w-4 h-4 text-primary" />
              EPCO Services
            </h3>
            <div className="space-y-4 text-sm text-muted-foreground">
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <span className="w-1 h-4 bg-primary rounded-full" />
                  Planning & Design
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1 h-4 bg-primary/60 rounded-full" />
                  Engineering & Construction
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1 h-4 bg-primary/40 rounded-full" />
                  Procurement & Investment
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1 h-4 bg-primary/20 rounded-full" />
                  Tourism Operations
                </li>
              </ul>

              <div className="pt-4 space-y-2 border-t border-border/50">
                <Link to="tel:+1800555AOTO" className="flex items-center gap-2 hover:text-primary transition-colors"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-phone w-4 h-4" aria-hidden="true" data-ast-id="f:/src/reusable_sections/Footer.tsx|sig:pqzi6e" data-ast-start="7741" data-ast-end="7770"><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path></svg>+00852-7019-1618</Link>
                <Link to="mailto:contact@aoto-global.com" className="flex items-center gap-2 hover:text-primary transition-colors"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-mail w-4 h-4" aria-hidden="true" data-ast-id="f:/src/reusable_sections/Footer.tsx|sig:qnroag" data-ast-start="8550" data-ast-end="8578"><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path><rect x="2" y="4" width="20" height="16" rx="2"></rect></svg>aotonexus26@gmail.com</Link>
              </div>
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-bold tracking-tight flex items-center gap-2 uppercase text-foreground">
              <Cpu className="w-4 h-4 text-primary" />
              Market Insights
            </h3>
            <p className="text-sm text-muted-foreground font-sans">
              Join our network of global investors to receive strategic updates on international tourism development and project trends.
            </p>
            
            <div className="bg-card/30 border border-border p-5 rounded-sm backdrop-blur-sm shadow-sm">
              <FormProvider formId="footer-newsletter" sectionName="footer-information" defaultValues={{
              email: ""
            }} successMessage="Your registration for our strategic mailing list is complete.">
                <NewsletterForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="border-t border-border bg-card/20 backdrop-blur-lg">
        <div className="container mx-auto px-4 py-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            
            {}
            <div className="flex items-center gap-2 order-2 md:order-1">
              <div className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
              </div>
              <span className="text-[10px] font-mono text-primary tracking-widest uppercase font-bold">
                Global Operations: Active
              </span>
            </div>

            {}
            <div className="text-[11px] text-muted-foreground order-3 md:order-2 text-center md:text-left font-sans uppercase tracking-wider">
              &copy; {currentYear} AOTO Global. All rights reserved. <span className="mx-2 text-border">|</span> <Link to="#" className="hover:text-primary transition-colors">Legal Notice</Link> <span className="mx-2 text-border">|</span> <Link to="#" className="hover:text-primary transition-colors">Privacy Policy</Link>
            </div>

            {}
            <div className="flex items-center gap-3 order-1 md:order-3">
              <Link to="#" className="p-2 rounded-full border border-border/50 text-muted-foreground hover:text-primary hover:border-primary transition-all" aria-label="Facebook">
                <Facebook className="w-3.5 h-3.5" />
              </Link>
              <Link to="#" className="p-2 rounded-full border border-border/50 text-muted-foreground hover:text-primary hover:border-primary transition-all" aria-label="Twitter">
                <Twitter className="w-3.5 h-3.5" />
              </Link>
              <Link to="#" className="p-2 rounded-full border border-border/50 text-muted-foreground hover:text-primary hover:border-primary transition-all" aria-label="LinkedIn">
                <Linkedin className="w-3.5 h-3.5" />
              </Link>
              <Link to="#" className="p-2 rounded-full border border-border/50 text-muted-foreground hover:text-primary hover:border-primary transition-all" aria-label="Instagram">
                <Instagram className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}